import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Formik, Form, Field, ErrorMessage } from 'formik';
import * as Yup from 'yup';
import { Icon } from '@iconify/react';
import AsyncSelect from 'react-select/async';

import Input from '@/components/forms/Input';
import Textarea from '@/components/forms/Textarea';
import Button from '@/components/ui/Button';
import Modal from '@/components/ui/Modal';
import Notification from '@/components/ui/Notification';
import showToast from '@/components/ui/Toast';
import { arenaService, SportCategory } from '@/services/arenaService';
import { wilayahService, KotaOption } from '@/services/wilayahService';
import communityService, { MABAR_FACILITIES_OPTIONS, CreateMabarEventPayload } from '@/services/communityService';
import { useScrollToFirstFormikErrorMobile } from '@/utils/useScrollToFirstFormikErrorMobile';
import { getKYCStatus, hasActivePaymentAccount } from '@/services/walletService';
import SparringForm from '@/components/community/SparringForm';
import { isSparringSupportedSport } from '@/types/sports';
import CustomFormSection from '@/components/community/CustomFormSection';
import type { CustomFormField } from '@/services/communityService';

interface PlayerRole {
  id: string;
  role: string;
  jumlahPemain: number;
  biayaPerOrang: number;
}

interface OperationalCost {
  id: string;
  deskripsi: string;
  biaya: number;
  jumlahItem?: number;
  biayaPerItem?: number;
}

interface CreateMabarFormValues {
  namaEvent: string;
  tautanEvent: string;
  deskripsiEvent: string;
  syaratKetentuan: string;
  tanggalEvent: string;
  jamMulai: string;
  jamSelesai: string;
  kota: string;
  lokasiArena: string;
  linkMapsVenue: string;
  linkGroup: string;
  tipeMabar: 'public' | 'private';
  kategoriOlahraga: string;
  biayaPemain: 'paid' | 'free';
  konfirmasiOtomatis?: 'yes' | 'no';
  cutoffHours: number;
  waitingList: boolean;
  waitingListSlots: number;
  paymentTime: 'awal' | 'akhir';
  paymentMethod: 'manual' | 'qris';
  facilities: string[];
  timeZone: 'WIB' | 'WITA' | 'WIT';
}

const urlRegex = /^(https?:\/\/)?([\w-]+\.)+[\w-]+(\/[\w\-._~:/?#[\]@!$&'()*+,;=.]*)?$/i;
const timeFormatRegex = /^([0-1][0-9]|2[0-3]):[0-5][0-9]$/;

const step1Schema = Yup.object({
  namaEvent: Yup.string().required('Nama event wajib diisi'),
  kategoriOlahraga: Yup.string().required('Kategori olahraga wajib dipilih'),
  deskripsiEvent: Yup.string().required('Deskripsi event wajib diisi'),
  tanggalEvent: Yup.string().required('Tanggal event wajib diisi'),
  jamMulai: Yup.string()
    .required('Jam mulai wajib diisi')
    .matches(timeFormatRegex, 'Format jam mulai tidak valid (contoh: 19.00)'),
  jamSelesai: Yup.string()
    .required('Jam selesai wajib diisi')
    .matches(timeFormatRegex, 'Format jam selesai tidak valid (contoh: 21.00)'),
  syaratKetentuan: Yup.string().required('Syarat dan ketentuan wajib diisi'),
});

const step2Schema = Yup.object({
  kota: Yup.string().required('Kota/Kabupaten wajib dipilih'),
  lokasiArena: Yup.string().required('Nama arena wajib diisi'),
  linkMapsVenue: Yup.string()
    .required('Link maps venue wajib diisi')
    .matches(urlRegex, 'Link maps harus berupa URL yang valid'),
});

const step3Schema = Yup.object({
  biayaPemain: Yup.string().oneOf(['free', 'paid']).required('Biaya pemain wajib dipilih'),
});

const getValidationSchema = (step: number) => {
  switch (step) {
    case 1:
      return step1Schema;
    case 2:
      return step1Schema.concat(step2Schema);
    case 3:
      return step1Schema.concat(step2Schema).concat(step3Schema);
    default:
      return Yup.object({});
  }
};

const getFieldsForStep = (step: number): (keyof CreateMabarFormValues)[] => {
  switch (step) {
    case 1:
      return [
        'namaEvent',
        'kategoriOlahraga',
        'deskripsiEvent',
        'tanggalEvent',
        'jamMulai',
        'jamSelesai',
        'syaratKetentuan',
      ];
    case 2:
      return ['kota', 'lokasiArena', 'linkMapsVenue'];
    case 3:
      return ['biayaPemain'];
    default:
      return [];
  }
};

const mobileFieldOrder: (keyof CreateMabarFormValues)[] = [
  'namaEvent',
  'kategoriOlahraga',
  'tanggalEvent',
  'timeZone',
  'jamMulai',
  'jamSelesai',
  'deskripsiEvent',
  'syaratKetentuan',
  'kota',
  'lokasiArena',
  'linkMapsVenue',
  'biayaPemain',
];

const ScrollToFirstError = () => {
  useScrollToFirstFormikErrorMobile(mobileFieldOrder);
  return null;
};

// Helper to pick icon for sport category
const getSportIcon = (name: string) => {
  const lower = name.toLowerCase();
  if (lower.includes('sepak') || lower.includes('football')) return 'ph:soccer-ball-duotone';
  if (lower.includes('mini')) return 'ph:soccer-ball-duotone';
  if (lower.includes('futsal')) return 'ph:soccer-ball-duotone';
  if (lower.includes('badminton')) return 'ph:tennis-ball-duotone';
  if (lower.includes('padel')) return 'ph:tennis-ball-duotone';
  if (lower.includes('tenis') || lower.includes('tennis')) return 'ph:tennis-ball-duotone';
  if (lower.includes('basket')) return 'ph:basketball-duotone';
  if (lower.includes('voli') || lower.includes('volley')) return 'ph:volleyball-duotone';
  return 'ph:trophy-duotone';
};

// Indonesian date display helper
const formatIndonesianDateDisplay = (dateStr: string) => {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return dateStr;
  const dayName = ['Minggu', 'Senin', 'Selasa', 'Rabu', 'Kamis', 'Jumat', 'Sabtu'][d.getDay()];
  const dayNum = d.getDate();
  const monthName = ['Jan', 'Feb', 'Mar', 'Apr', 'Mei', 'Jun', 'Jul', 'Agus', 'Sep', 'Okt', 'Nov', 'Des'][d.getMonth()];
  const year = d.getFullYear();
  return `${dayName}, ${dayNum} ${monthName} ${year}`;
};

// Indonesian 24h time display helper
const formatIndonesianTimeDisplay = (timeStr: string) => {
  if (!timeStr) return '';
  return timeStr.replace(':', '.');
};

const parseIndonesianTimeToDB = (val: string) => {
  if (!val) return '';
  // Remove non-numeric characters except dot and colon
  let clean = val.replace(/[^0-9.:]/g, '');

  const digits = clean.replace(/\D/g, '');
  if (!clean.includes('.') && !clean.includes(':')) {
    if (digits.length === 4) {
      let h = Math.min(23, parseInt(digits.slice(0, 2), 10) || 0);
      let m = Math.min(59, parseInt(digits.slice(2, 4), 10) || 0);
      return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
    }
    return clean;
  }

  const parts = clean.split(/[.:]/);
  let hStr = parts[0] ? parts[0].slice(0, 2) : '';
  let mStr = parts[1] ? parts[1].slice(0, 2) : '';

  if (hStr.length === 2) {
    let h = Math.min(23, parseInt(hStr, 10) || 0);
    hStr = String(h).padStart(2, '0');
  }
  if (mStr.length === 2) {
    let m = Math.min(59, parseInt(mStr, 10) || 0);
    mStr = String(m).padStart(2, '0');
  }

  return parts.length > 1 ? `${hStr}:${mStr}` : hStr;
};

const CreateMabarCommunityPage = () => {
  const navigate = useNavigate();
  const [initialValues, setInitialValues] = useState<CreateMabarFormValues | null>(null);
  const [hasActivePayment, setHasActivePayment] = useState<boolean>(true);
  const [currentStep, setCurrentStep] = useState(1);
  const steps = [
    { id: 1, label: 'Informasi Event' },
    { id: 2, label: 'Lokasi & Fasilitas' },
    { id: 3, label: 'Pengaturan Tambahan' },
  ];

  const [sportCategories, setSportCategories] = useState<SportCategory[]>([]);
  const [showAllSports, setShowAllSports] = useState(false);

  // Custom Form State
  const [customFormEnabled, setCustomFormEnabled] = useState<boolean>(false);
  const [customFormFields, setCustomFormFields] = useState<CustomFormField[]>([]);

  // Player Roles State (Inline inside step 3)
  const [playerRoles, setPlayerRoles] = useState<PlayerRole[]>([]);

  // Operational Costs State
  const [operationalCosts, setOperationalCosts] = useState<OperationalCost[]>([]);

  // Date Picker Bottom Sheet State
  const [isDatePickerOpen, setIsDatePickerOpen] = useState(false);
  const [calendarYear, setCalendarYear] = useState(new Date().getFullYear());
  const [calendarMonth, setCalendarMonth] = useState(new Date().getMonth());

  const indonesianMonths = [
    'Januari', 'Februari', 'Maret', 'April', 'Mei', 'Juni',
    'Juli', 'Agustus', 'September', 'Oktober', 'November', 'Desember'
  ];

  const dayNamesIndonesian = ['SEN', 'SEL', 'RAB', 'KAM', 'JUM', 'SAB', 'MIN'];

  const getDaysInMonthMatrix = (year: number, month: number) => {
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    let startDay = firstDay.getDay();
    startDay = startDay === 0 ? 6 : startDay - 1;
    const totalDays = lastDay.getDate();
    const matrix: (number | null)[] = [];
    for (let i = 0; i < startDay; i++) {
      matrix.push(null);
    }
    for (let d = 1; d <= totalDays; d++) {
      matrix.push(d);
    }
    return matrix;
  };

  // Modals & Confirmation State
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [pendingFormValues, setPendingFormValues] = useState<CreateMabarFormValues | null>(null);

  // Custom Event Slug Check State
  const [hasCustomSlug, setHasCustomSlug] = useState(false);
  const [slugCheckStatus, setSlugCheckStatus] = useState<
    'idle' | 'checking' | 'available' | 'taken' | 'invalid'
  >('idle');
  const [isCheckingSlug, setIsCheckingSlug] = useState(false);
  const slugDebounceRef = useRef<NodeJS.Timeout | null>(null);

  // Notification State
  const [notification, setNotification] = useState<{
    isOpen: boolean;
    title: string;
    message: string;
    variant: 'success' | 'error';
  }>({
    isOpen: false,
    title: '',
    message: '',
    variant: 'success',
  });

  const debounceRef = useRef<NodeJS.Timeout | null>(null);

  // Sparring state
  const [isSparring, setIsSparring] = useState(false);
  const [teamAName, setTeamAName] = useState('');
  const [teamAColor, setTeamAColor] = useState('#FC480D');
  const [teamBName, setTeamBName] = useState('');
  const [teamBColor, setTeamBColor] = useState('#3B7DDB');

  // Load Initial Data
  useEffect(() => {
    const initData = async () => {
      try {
        const [categories, kycStatus, profileRes] = await Promise.all([
          arenaService.getSportCategories('create-mabar-community'),
          getKYCStatus(),
          communityService.getProfile().catch(() => null),
        ]);

        setSportCategories(categories);
        const hasPaymentAccount = hasActivePaymentAccount(kycStatus);
        setHasActivePayment(hasPaymentAccount);

        if (profileRes?.data?.community?.name) {
          setTeamAName(profileRes.data.community.name);
        } else {
          const localName = localStorage.getItem('communityName') || '';
          if (localName) setTeamAName(localName);
        }

        // Find default category for Mini Soccer
        const miniSoccerCat = categories.find(
          (c) =>
            c.slug === 'mini-soccer' ||
            c.slug === 'minisoccer' ||
            c.name.toLowerCase().includes('mini soccer')
        ) || categories[0];

        const defaultSportSlug = miniSoccerCat?.slug || 'mini-soccer';

        setInitialValues({
          namaEvent: '',
          tautanEvent: '',
          deskripsiEvent: '',
          syaratKetentuan: '',
          tanggalEvent: '',
          jamMulai: '',
          jamSelesai: '',
          kota: '',
          lokasiArena: '',
          linkMapsVenue: '',
          linkGroup: '',
          tipeMabar: 'public',
          kategoriOlahraga: defaultSportSlug,
          biayaPemain: 'paid',
          konfirmasiOtomatis: 'yes',
          cutoffHours: 2,
          waitingList: false,
          waitingListSlots: 6,
          paymentTime: hasPaymentAccount ? 'awal' : 'akhir',
          paymentMethod: hasPaymentAccount ? 'qris' : 'manual',
          facilities: [],
          timeZone: 'WIB',
        });
      } catch (error) {
        console.error('Error fetching categories/KYC:', error);
      }
    };

    initData();
  }, []);

  const calculateTotalSlots = () => {
    return playerRoles.reduce((sum, role) => sum + role.jumlahPemain, 0);
  };

  const calculateTotalOperationalCost = () => {
    return operationalCosts.reduce((sum, cost) => {
      const qty = cost.jumlahItem ?? 1;
      const unitPrice = cost.biayaPerItem ?? cost.biaya ?? 0;
      return sum + qty * unitPrice;
    }, 0);
  };

  const formatCurrency = (value: number) => {
    if (value === 0) return 'Free';
    return `Rp. ${value.toLocaleString('id-ID')}`;
  };

  // Debounced Slug Check Function
  const handleCheckSlug = useCallback((slug: string) => {
    if (slugDebounceRef.current) {
      clearTimeout(slugDebounceRef.current);
    }

    const cleanSlug = slug.trim().toLowerCase();
    if (cleanSlug.length < 3) {
      setSlugCheckStatus('invalid');
      setIsCheckingSlug(false);
      return;
    }

    setIsCheckingSlug(true);
    setSlugCheckStatus('checking');

    slugDebounceRef.current = setTimeout(async () => {
      try {
        const res = await communityService.checkEventSlugAvailability(cleanSlug);
        if (res.available) {
          setSlugCheckStatus('available');
        } else {
          setSlugCheckStatus('taken');
        }
      } catch {
        setSlugCheckStatus('taken');
      } finally {
        setIsCheckingSlug(false);
      }
    }, 500);
  }, []);

  const searchKota = useCallback(
    (inputValue: string, callback: (options: KotaOption[]) => void) => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }

      if (inputValue.length < 3) {
        callback([]);
        return;
      }

      debounceRef.current = setTimeout(async () => {
        const options = await wilayahService.searchKota(inputValue);
        callback(options);
      }, 500);
    },
    []
  );

  const kotaSelectStyles = {
    control: (provided: any, state: any) => ({
      ...provided,
      borderRadius: '0.75rem',
      borderColor: state.isFocused ? '#FC480D' : '#E5E7EB',
      boxShadow: state.isFocused ? '0 0 0 2px rgba(252, 72, 13, 0.2)' : 'none',
      '&:hover': {
        borderColor: state.isFocused ? '#FC480D' : '#D1D5DB',
      },
      minHeight: '44px',
      fontSize: '0.875rem',
      fontWeight: 500,
      color: '#111827',
    }),
    singleValue: (provided: any) => ({
      ...provided,
      fontSize: '0.875rem',
      fontWeight: 500,
      color: '#111827',
    }),
    input: (provided: any) => ({
      ...provided,
      fontSize: '0.875rem',
      fontWeight: 500,
      color: '#111827',
    }),
    placeholder: (provided: any) => ({
      ...provided,
      fontSize: '0.875rem',
      fontWeight: 400,
      color: '#9CA3AF',
    }),
    option: (provided: any, state: any) => ({
      ...provided,
      backgroundColor: state.isSelected ? '#FC480D' : state.isFocused ? '#FEF2F2' : 'white',
      color: state.isSelected ? 'white' : '#374151',
      fontSize: '0.875rem',
      fontWeight: 500,
    }),
  };

  const handleSubmit = (values: CreateMabarFormValues) => {
    if (currentStep < 3) return;

    if (playerRoles.length === 0) {
      setNotification({
        isOpen: true,
        title: 'Error',
        message: 'Silakan buat minimal satu posisi pemain',
        variant: 'error',
      });
      return;
    }

    if (slugCheckStatus === 'taken') {
      setNotification({
        isOpen: true,
        title: 'Error',
        message: 'Tautan event sudah digunakan. Silakan pilih tautan yang lain.',
        variant: 'error',
      });
      return;
    }

    setPendingFormValues(values);
    setIsConfirmModalOpen(true);
  };

  const handleConfirmedSave = async () => {
    if (!pendingFormValues) return;

    setIsSubmitting(true);
    setIsConfirmModalOpen(false);

    try {
      const player_roles = playerRoles.map((role) => ({
        role: role.role || 'Pemain',
        slots: role.jumlahPemain,
        price: pendingFormValues.biayaPemain === 'free' ? 0 : role.biayaPerOrang,
      }));

      const selectedCat = sportCategories.find(
        (c) => c.slug === pendingFormValues.kategoriOlahraga
      );
      const isSparringSport = isSparringSupportedSport(selectedCat);

      const config = {
        player_roles,
        auto_confirm: pendingFormValues.konfirmasiOtomatis === 'yes',
        allow_guest: false,
        max_guests_per_user: 0,
        operational_costs: operationalCosts.map((cost) => ({
          name: cost.deskripsi,
          amount: cost.biaya,
        })),
        payment_time: pendingFormValues.paymentTime,
        payment_method: pendingFormValues.paymentMethod,
        cutoff_hours: pendingFormValues.cutoffHours,
        waiting_list: pendingFormValues.waitingList,
        waiting_list_slots: pendingFormValues.waitingList ? pendingFormValues.waitingListSlots : 0,
        is_sparring: isSparringSport ? isSparring : false,
        teams:
          isSparringSport && isSparring
            ? [
                { name: teamAName || 'Tim A', jersey_color: teamAColor || '#FC480D' },
                { name: teamBName || 'Tim B', jersey_color: teamBColor || '#3B7DDB' },
              ]
            : [],
        facilities: pendingFormValues.facilities || [],
        time_zone: pendingFormValues.timeZone,
      };

      const payload: CreateMabarEventPayload = {
        name: pendingFormValues.namaEvent,
        slug: pendingFormValues.tautanEvent ? pendingFormValues.tautanEvent.trim() : undefined,
        description: pendingFormValues.deskripsiEvent,
        tnc: pendingFormValues.syaratKetentuan,
        event_date: pendingFormValues.tanggalEvent,
        start_time: pendingFormValues.jamMulai,
        end_time: pendingFormValues.jamSelesai,
        lokasi_arena: pendingFormValues.lokasiArena,
        address: pendingFormValues.lokasiArena,
        city: pendingFormValues.kota,
        map_link: pendingFormValues.linkMapsVenue,
        sport_category_slug: pendingFormValues.kategoriOlahraga,
        link_group: pendingFormValues.linkGroup || '',
        type: pendingFormValues.biayaPemain,
        visibility: pendingFormValues.tipeMabar,
        config: config as any,
        custom_form: customFormEnabled && customFormFields.length > 0 ? customFormFields : undefined,
      };

      await communityService.createEvent(payload);

      showToast.success('Event mabar berhasil dibuat!');

      setTimeout(() => {
        navigate('/host/mabar');
      }, 1500);
    } catch (error: any) {
      setNotification({
        isOpen: true,
        title: 'Error',
        message: error.message || 'Gagal membuat event mabar',
        variant: 'error',
      });
    } finally {
      setIsSubmitting(false);
    }
  };


  const handleNextStep = async (validateForm: any, setFieldTouched: any) => {
    const errors = await validateForm();
    const fieldsToValidate = getFieldsForStep(currentStep);
    fieldsToValidate.forEach((field) => {
      setFieldTouched(field, true, true);
    });
    const hasErrors = fieldsToValidate.some((field) => !!errors[field]);
    if (!hasErrors) {
      setCurrentStep((prev) => Math.min(3, prev + 1));
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  if (!initialValues) {
    return (
      <div className="flex items-center justify-center h-screen bg-gray-50">
        <Icon icon="ph:spinner-bold" className="w-10 h-10 animate-spin text-[#FC480D]" />
      </div>
    );
  }

  // Categories list split into top pills and expand list
  const defaultSportList = ['Sepak Bola', 'Mini Soccer', 'Futsal', 'Badminton', 'Padel', 'Tenis'];
  const primaryCategories = sportCategories.filter((cat) =>
    defaultSportList.some(
      (name) => name.toLowerCase() === cat.name.toLowerCase() || cat.slug?.includes(name.toLowerCase())
    )
  );

  const visibleCategories = showAllSports
    ? sportCategories
    : primaryCategories.length > 0
    ? primaryCategories
    : sportCategories.slice(0, 6);

  const otherCategories = sportCategories.filter(
    (c) => !visibleCategories.some((vc) => vc.slug === c.slug)
  );

  return (
    <div className="min-h-screen bg-gray-50/50 pb-28">
      {/* Header Container */}
      <div className="bg-white border-b border-gray-200 sticky top-0 z-30 shadow-2xs">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-3.5">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={() => {
                  if (currentStep > 1) {
                    setCurrentStep((prev) => prev - 1);
                  } else {
                    navigate('/host/mabar');
                  }
                }}
                className="p-2 -ml-2 rounded-full text-gray-600 hover:bg-gray-100 transition-colors"
                title="Kembali"
              >
                <Icon icon="ph:arrow-left-bold" className="w-5 h-5" />
              </button>
              <div>
                <h1 className="text-base sm:text-lg font-bold text-gray-900 leading-tight">
                  Buat Event Mabar
                </h1>
                <p className="text-xs text-gray-500 font-medium hidden sm:block">
                  Lengkapi detail event untuk memublikasikan kegiatan mabar komunitas Anda
                </p>
                <p className="text-xs text-gray-500 font-medium sm:hidden">
                  Langkah {currentStep} dari 3 · {steps[currentStep - 1].label}
                </p>
              </div>
            </div>

            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => navigate('/host/mabar')}
                className="p-2 -mr-2 rounded-full text-gray-600 hover:bg-gray-100 transition-colors"
                title="Tutup"
              >
                <Icon icon="ph:x-bold" className="w-5 h-5" />
              </button>
            </div>
          </div>

          {/* Desktop Stepper Bar */}
          <div className="hidden md:grid grid-cols-3 gap-4 mt-4 pt-3 border-t border-gray-100">
            {steps.map((step) => {
              const isActive = currentStep === step.id;
              const isCompleted = currentStep > step.id;
              const isClickable = step.id <= currentStep;

              return (
                <button
                  key={step.id}
                  type="button"
                  disabled={!isClickable}
                  onClick={() => isClickable && setCurrentStep(step.id)}
                  className={`flex items-center gap-3 p-2.5 rounded-xl border text-left transition-all ${
                    isActive
                      ? 'bg-orange-50/70 border-[#FC480D] text-[#FC480D]'
                      : isCompleted
                      ? 'bg-emerald-50/50 border-emerald-200 text-emerald-700 cursor-pointer hover:bg-emerald-50'
                      : 'bg-gray-50 border-gray-200 text-gray-400 cursor-not-allowed'
                  }`}
                >
                  <div
                    className={`w-7 h-7 rounded-lg flex items-center justify-center text-xs font-bold shrink-0 ${
                      isActive
                        ? 'bg-[#FC480D] text-white'
                        : isCompleted
                        ? 'bg-emerald-600 text-white'
                        : 'bg-gray-200 text-gray-500'
                    }`}
                  >
                    {isCompleted ? <Icon icon="ph:check-bold" className="w-4 h-4" /> : step.id}
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-xs font-medium uppercase tracking-wider opacity-75">
                      Langkah {step.id}
                    </p>
                    <p className="text-xs sm:text-sm font-bold truncate">{step.label}</p>
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        {/* Mobile Progress Line */}
        <div className="w-full bg-gray-100 h-1 md:hidden">
          <div
            className="bg-[#FC480D] h-full transition-all duration-300"
            style={{ width: `${(currentStep / 3) * 100}%` }}
          />
        </div>
      </div>

      {/* Main Content Area */}
      <div className="max-w-5xl mx-auto p-4 sm:p-6 lg:p-8">
        <Formik
          initialValues={initialValues}
          validationSchema={getValidationSchema(currentStep)}
          onSubmit={handleSubmit}
        >
          {({ values, setFieldValue, validateForm, setFieldTouched }) => (
            <Form className="space-y-6">
              <ScrollToFirstError />

              {/* STEP 1: INFORMASI EVENT */}
              {currentStep === 1 && (
                <div className="bg-white rounded-2xl border border-gray-200/80 p-5 sm:p-8 space-y-6 shadow-xs">
                  <div className="border-b border-gray-100 pb-4 flex items-center justify-between">
                    <div>
                      <h3 className="text-lg font-bold text-gray-900">Informasi Event</h3>
                      <p className="text-xs text-gray-500 mt-0.5">Isi detail umum tentang event mabar yang akan dilaksanakan.</p>
                    </div>
                    <span className="hidden sm:inline-flex items-center gap-1 text-xs font-semibold px-2.5 py-1 rounded-full bg-orange-50 text-[#FC480D] border border-orange-200">
                      Langkah 1 dari 3
                    </span>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* Nama Event */}
                    <div className="md:col-span-2">
                      <Input
                        name="namaEvent"
                        label="Nama Event"
                        placeholder="Contoh: Mabar Futsal Garuda Muda"
                        required
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                          setFieldValue('namaEvent', e.target.value);
                          if (!hasCustomSlug && e.target.value) {
                            const autoSlug = e.target.value
                              .toLowerCase()
                              .trim()
                              .replace(/[^a-z0-9\s-]/g, '')
                              .replace(/[\s_]+/g, '-')
                              .replace(/^-+|-+$/g, '');
                            setFieldValue('tautanEvent', autoSlug);
                            handleCheckSlug(autoSlug);
                          }
                        }}
                      />
                    </div>

                    {/* Kategori Olahraga Pills */}
                    <div className="md:col-span-2 space-y-2.5" data-field-anchor="kategoriOlahraga">
                      <label className="block text-sm font-bold text-gray-900">
                        Kategori Olahraga <span className="text-red-500">*</span>
                      </label>
                      <div className="flex flex-wrap gap-2">
                        {visibleCategories.map((cat) => {
                          const isSelected = values.kategoriOlahraga === cat.slug;
                          return (
                            <button
                              key={cat.id || cat.slug}
                              type="button"
                              onClick={() => {
                                setFieldValue('kategoriOlahraga', cat.slug);
                              }}
                              className={`px-4 py-2 rounded-full text-xs sm:text-sm font-semibold flex items-center gap-2 transition-all cursor-pointer ${
                                isSelected
                                  ? 'bg-[#FC480D] border border-[#FC480D] text-white shadow-xs'
                                  : 'bg-white border border-gray-200 text-gray-700 hover:border-gray-300 hover:bg-gray-50'
                              }`}
                            >
                              <Icon
                                icon={getSportIcon(cat.name)}
                                className={`w-4 h-4 ${isSelected ? 'text-white' : 'text-gray-500'}`}
                              />
                              <span>{cat.name}</span>
                            </button>
                          );
                        })}

                        {otherCategories.length > 0 && (
                          <button
                            type="button"
                            onClick={() => setShowAllSports((prev) => !prev)}
                            className={`px-4 py-2 rounded-full text-xs sm:text-sm font-semibold flex items-center gap-1.5 transition-all cursor-pointer ${
                              showAllSports
                                ? 'bg-gray-800 border border-gray-800 text-white'
                                : 'bg-white border border-gray-200 text-gray-700 hover:border-gray-300'
                            }`}
                          >
                            <span>{showAllSports ? 'Sembunyikan' : 'Lainnya'}</span>
                            <Icon
                              icon={showAllSports ? 'ph:caret-up-bold' : 'ph:caret-down-bold'}
                              className="w-3.5 h-3.5"
                            />
                          </button>
                        )}
                      </div>
                      <ErrorMessage
                        name="kategoriOlahraga"
                        component="div"
                        className="text-red-500 text-xs mt-1"
                      />
                    </div>

                    {/* Tanggal Event */}
                    <div className="space-y-2" data-field-anchor="tanggalEvent">
                      <label className="block text-sm font-bold text-gray-900">
                        Tanggal <span className="text-red-500">*</span>
                      </label>
                      <button
                        type="button"
                        onClick={() => setIsDatePickerOpen(true)}
                        className="w-full flex items-center justify-between border border-gray-300 rounded-xl px-3.5 py-2.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#FC480D] transition-all cursor-pointer h-[42px]"
                      >
                        <div className="flex items-center gap-2.5">
                          <Icon icon="ph:calendar-dots-bold" className="w-5 h-5 text-[#FC480D]" />
                          <span className={values.tanggalEvent ? 'font-medium text-gray-900' : 'text-[#8C8C8C]'}>
                            {values.tanggalEvent ? formatIndonesianDateDisplay(values.tanggalEvent) : 'Pilih tanggal'}
                          </span>
                        </div>
                        <Icon icon="ph:caret-down-bold" className="w-4 h-4 text-gray-400" />
                      </button>
                      <ErrorMessage
                        name="tanggalEvent"
                        component="div"
                        className="text-red-500 text-xs mt-1"
                      />
                    </div>

                    {/* Zona Waktu */}
                    <div className="space-y-2" data-field-anchor="timeZone">
                      <label className="block text-sm font-bold text-gray-900">
                        Zona Waktu <span className="text-red-500">*</span>
                      </label>
                      <div className="relative">
                        <span className="absolute left-3.5 top-1/2 -translate-y-1/2 text-[#FC480D] pointer-events-none z-10">
                          <Icon icon="ph:globe-hemisphere-east-bold" className="w-5 h-5" />
                        </span>
                        <Field
                          as="select"
                          name="timeZone"
                          className="w-full border border-gray-300 rounded-xl pl-11 pr-8 py-2.5 text-sm font-medium text-gray-900 focus:outline-none focus:ring-2 focus:ring-[#FC480D] bg-white h-[42px] cursor-pointer appearance-none"
                        >
                          <option value="WIB">WIB (UTC+7)</option>
                          <option value="WITA">WITA (UTC+8)</option>
                          <option value="WIT">WIT (UTC+9)</option>
                        </Field>
                        <span className="absolute right-3.5 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none">
                          <Icon icon="ph:caret-down-bold" className="w-4 h-4" />
                        </span>
                      </div>
                    </div>

                    {/* Waktu Mulai & Waktu Selesai */}
                    <div className="md:col-span-2 grid grid-cols-2 gap-4">
                      {/* Waktu Mulai */}
                      <div className="space-y-2" data-field-anchor="jamMulai">
                        <label className="block text-sm font-bold text-gray-900">
                          Waktu Mulai <span className="text-red-500">*</span>
                        </label>
                        <div className="relative">
                          <span className="absolute left-3.5 top-1/2 -translate-y-1/2 text-[#FC480D] pointer-events-none z-10">
                            <Icon icon="ph:clock-bold" className="w-5 h-5" />
                          </span>
                          <input
                            type="text"
                            name="jamMulai"
                            inputMode="numeric"
                            value={formatIndonesianTimeDisplay(values.jamMulai)}
                            onChange={(e) => {
                              const val = e.target.value;
                              setFieldValue('jamMulai', parseIndonesianTimeToDB(val));
                            }}
                            placeholder="19.00"
                            className="w-full border border-gray-300 rounded-xl pl-11 pr-3.5 py-2.5 text-sm font-medium text-gray-900 focus:outline-none focus:ring-2 focus:ring-[#FC480D] h-[42px]"
                          />
                        </div>
                        <ErrorMessage
                          name="jamMulai"
                          component="div"
                          className="text-red-500 text-xs mt-1"
                        />
                      </div>

                      {/* Waktu Selesai */}
                      <div className="space-y-2" data-field-anchor="jamSelesai">
                        <label className="block text-sm font-bold text-gray-900">
                          Waktu Selesai <span className="text-red-500">*</span>
                        </label>
                        <div className="relative">
                          <span className="absolute left-3.5 top-1/2 -translate-y-1/2 text-[#FC480D] pointer-events-none z-10">
                            <Icon icon="ph:clock-bold" className="w-5 h-5" />
                          </span>
                          <input
                            type="text"
                            name="jamSelesai"
                            inputMode="numeric"
                            value={formatIndonesianTimeDisplay(values.jamSelesai)}
                            onChange={(e) => {
                              const val = e.target.value;
                              setFieldValue('jamSelesai', parseIndonesianTimeToDB(val));
                            }}
                            placeholder="21.00"
                            className="w-full border border-gray-300 rounded-xl pl-11 pr-3.5 py-2.5 text-sm font-medium text-gray-900 focus:outline-none focus:ring-2 focus:ring-[#FC480D] h-[42px]"
                          />
                        </div>
                        <ErrorMessage
                          name="jamSelesai"
                          component="div"
                          className="text-red-500 text-xs mt-1"
                        />
                      </div>
                    </div>

                    {/* Link Grup Komunitas */}
                    <div className="space-y-2">
                      <label className="block text-sm font-bold text-gray-900">
                        Link Grup Komunitas
                      </label>
                      <div className="relative">
                        <span className="absolute left-3.5 top-1/2 -translate-y-1/2 text-emerald-500">
                          <Icon icon="ph:whatsapp-logo-bold" className="w-5 h-5" />
                        </span>
                        <Field
                          name="linkGroup"
                          type="text"
                          placeholder="chat.whatsapp.com/..."
                          className="w-full border border-gray-300 rounded-xl pl-11 pr-4 py-2.5 text-sm font-medium text-gray-900 focus:outline-none focus:ring-2 focus:ring-[#FC480D]"
                        />
                      </div>
                    </div>

                    {/* Tautan Event (Custom Slug with API Check) */}
                    <div className="space-y-2">
                      <label className="block text-sm font-bold text-gray-900">
                        Tautan Event
                      </label>
                      <div className="flex rounded-xl border border-gray-300 overflow-hidden focus-within:ring-2 focus-within:ring-[#FC480D] focus-within:border-transparent transition-all">
                        <span className="bg-gray-100 text-gray-500 px-3.5 py-2.5 text-xs sm:text-sm flex items-center font-medium border-r border-gray-200 select-none shrink-0">
                          sporto.id/event/
                        </span>
                        <div className="relative flex-1 flex items-center">
                          <input
                            type="text"
                            name="tautanEvent"
                            value={values.tautanEvent}
                            onChange={(e) => {
                              const raw = e.target.value;
                              const formatted = raw.toLowerCase().replace(/[^a-z0-9-]/g, '');
                              setFieldValue('tautanEvent', formatted);
                              setHasCustomSlug(true);
                              handleCheckSlug(formatted);
                            }}
                            placeholder="mabar-futsal-garuda"
                            className="w-full px-3 py-2.5 text-sm font-medium focus:outline-none text-gray-900 bg-white"
                          />
                          <div className="absolute right-3">
                            {isCheckingSlug ? (
                              <Icon
                                icon="ph:spinner-bold"
                                className="w-4 h-4 animate-spin text-gray-400"
                              />
                            ) : slugCheckStatus === 'available' ? (
                              <Icon
                                icon="ph:check-circle-fill"
                                className="w-4 h-4 text-green-500"
                              />
                            ) : slugCheckStatus === 'taken' ? (
                              <Icon icon="ph:x-circle-fill" className="w-4 h-4 text-red-500" />
                            ) : null}
                          </div>
                        </div>
                      </div>
                      {slugCheckStatus === 'available' && (
                        <p className="text-xs text-green-600 font-medium flex items-center gap-1">
                          <Icon icon="ph:check-bold" className="w-3.5 h-3.5" /> Tautan dapat digunakan
                        </p>
                      )}
                      {slugCheckStatus === 'taken' && (
                        <p className="text-xs text-red-500 font-medium flex items-center gap-1">
                          <Icon icon="ph:warning-circle-bold" className="w-3.5 h-3.5" /> Tautan sudah digunakan
                        </p>
                      )}
                      {slugCheckStatus === 'invalid' && values.tautanEvent.length > 0 && (
                        <p className="text-xs text-amber-600 font-medium">
                          Minimal 3 karakter, hanya huruf, angka, dan strip.
                        </p>
                      )}
                    </div>

                    {/* Deskripsi */}
                    <div className="md:col-span-1">
                      <Textarea
                        name="deskripsiEvent"
                        label="Deskripsi"
                        placeholder="Ceritakan tentang event ini."
                        rows={5}
                        required
                      />
                    </div>

                    {/* Syarat & Ketentuan */}
                    <div className="md:col-span-1">
                      <Textarea
                        name="syaratKetentuan"
                        label="Syarat & Ketentuan"
                        placeholder="Contoh: Pembayaran maksimal H-1, tidak ada refund jika batal mendadak."
                        rows={5}
                        required
                      />
                    </div>

                    {/* Visibilitas */}
                    <div className="md:col-span-2 space-y-2">
                      <label className="block text-sm font-bold text-gray-900">Visibilitas</label>
                      <p className="text-xs text-gray-500">
                        Private hanya bisa diakses lewat tautan undangan.
                      </p>
                      <div className="flex gap-3 pt-1">
                        <button
                          type="button"
                          onClick={() => setFieldValue('tipeMabar', 'public')}
                          className={`px-6 py-2.5 rounded-full text-xs sm:text-sm font-semibold transition-all cursor-pointer ${
                            values.tipeMabar === 'public'
                              ? 'bg-[#FC480D] text-white border border-[#FC480D] shadow-xs'
                              : 'bg-white border border-gray-200 text-gray-600 hover:border-gray-300'
                          }`}
                        >
                          Umum
                        </button>
                        <button
                          type="button"
                          onClick={() => setFieldValue('tipeMabar', 'private')}
                          className={`px-6 py-2.5 rounded-full text-xs sm:text-sm font-semibold transition-all cursor-pointer ${
                            values.tipeMabar === 'private'
                              ? 'bg-[#FC480D] text-white border border-[#FC480D] shadow-xs'
                              : 'bg-white border border-gray-200 text-gray-600 hover:border-gray-300'
                          }`}
                        >
                          Private
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* STEP 2: LOKASI & FASILITAS */}
              {currentStep === 2 && (
                <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
                  {/* Lokasi Card */}
                  <div className="md:col-span-7 bg-white rounded-2xl border border-gray-200/80 p-5 sm:p-6 space-y-6 shadow-xs h-fit">
                    <div className="border-b border-gray-100 pb-3 flex items-center justify-between">
                      <h3 className="text-lg font-bold text-gray-900">Lokasi Event</h3>
                      <span className="hidden sm:inline-flex items-center gap-1 text-xs font-semibold px-2.5 py-1 rounded-full bg-orange-50 text-[#FC480D] border border-orange-200">
                        Langkah 2 dari 3
                      </span>
                    </div>

                    {/* Kota */}
                    <div className="w-full !mt-0" data-field-anchor="kota">
                      <label className="block text-sm font-bold text-gray-900 mb-2">
                        Kota <span className="text-red-500">*</span>
                      </label>
                      <AsyncSelect
                        cacheOptions
                        loadOptions={searchKota}
                        defaultOptions
                        value={
                          values.kota
                            ? { nama: values.kota, namaLengkap: values.kota, value: values.kota }
                            : null
                        }
                        onChange={(option: any) => setFieldValue('kota', option?.nama || '')}
                        placeholder="Ketik minimal 3 huruf untuk mencari..."
                        noOptionsMessage={({ inputValue }: any) =>
                          inputValue && inputValue.length < 3
                            ? 'Ketik minimal 3 huruf'
                            : 'Tidak ada hasil ditemukan'
                        }
                        loadingMessage={() => 'Mencari...'}
                        isClearable
                        styles={kotaSelectStyles}
                        getOptionLabel={(option: any) => option.namaLengkap}
                        getOptionValue={(option: any) => option.value}
                      />
                      <ErrorMessage
                        name="kota"
                        component="div"
                        className="text-red-500 text-xs mt-1"
                      />
                    </div>

                    {/* Nama Lokasi */}
                    <div className="!mt-4" data-field-anchor="lokasiArena">
                      <Input
                        name="lokasiArena"
                        label="Nama Lokasi"
                        placeholder="Contoh: Gor Senayan Yogyakarta"
                        required
                      />
                    </div>

                    {/* Link Maps */}
                    <div className="space-y-2 !mt-4">
                      <label className="block text-sm font-bold text-gray-900">
                        Link Maps <span className="text-red-500">*</span>
                      </label>
                      <div className="relative">
                        <span className="absolute left-3.5 top-1/2 -translate-y-1/2 text-orange-500">
                          <Icon icon="ph:map-pin-bold" className="w-5 h-5" />
                        </span>
                        <Field
                          name="linkMapsVenue"
                          type="text"
                          placeholder="maps.app.goo.gl/..."
                          className="w-full border border-gray-300 rounded-xl pl-11 pr-4 py-2.5 text-sm font-medium text-gray-900 focus:outline-none focus:ring-2 focus:ring-[#FC480D]"
                        />
                      </div>
                      <p className="text-xs text-gray-400">
                        Booking lapangan otomatis belum tersedia — isi lokasi secara manual.
                      </p>
                      <ErrorMessage
                        name="linkMapsVenue"
                        component="div"
                        className="text-red-500 text-xs mt-1"
                      />
                    </div>
                  </div>

                  {/* Fasilitas Card */}
                  <div className="md:col-span-5 bg-white rounded-2xl border border-gray-200/80 p-5 sm:p-6 space-y-4 shadow-xs h-fit">
                    <div className="flex items-center justify-between border-b border-gray-100 pb-3">
                      <h4 className="text-base font-bold text-gray-900">Fasilitas Mabar</h4>
                      <span className="text-xs font-semibold px-2.5 py-1 rounded-full bg-gray-100 text-gray-600">
                        {values.facilities?.length || 0} dipilih
                      </span>
                    </div>
                    <p className="text-xs text-gray-500">
                      Pilih fasilitas yang disediakan host untuk event ini.
                    </p>
                    <div className="flex flex-wrap gap-2 pt-1">
                      {MABAR_FACILITIES_OPTIONS.map((facility) => {
                        const isSelected = (values.facilities || []).includes(facility.id);
                        return (
                          <button
                            key={facility.id}
                            type="button"
                            onClick={() => {
                              const current = values.facilities || [];
                              const next = isSelected
                                ? current.filter((f: string) => f !== facility.id)
                                : [...current, facility.id];
                              setFieldValue('facilities', next);
                            }}
                            className={`px-4 py-2 rounded-full text-xs sm:text-sm font-semibold flex items-center gap-1.5 transition-all cursor-pointer ${
                              isSelected
                                ? 'bg-[#FC480D] border border-[#FC480D] text-white shadow-xs'
                                : 'bg-white border border-gray-200 text-gray-600 hover:border-gray-300'
                            }`}
                          >
                            <Icon
                              icon={isSelected ? 'ph:check-circle-fill' : 'ph:plus-circle-bold'}
                              className={`w-4 h-4 ${isSelected ? 'text-white' : 'text-gray-400'}`}
                            />
                            <span>{facility.label}</span>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                </div>
              )}

              {/* STEP 3: PENGATURAN TAMBAHAN */}
              {currentStep === 3 && (
                <div className="space-y-6">
                  {/* Top Notice Banner */}
                  <div className="p-4 rounded-xl bg-orange-50 border border-orange-100 flex items-start gap-3">
                    <Icon
                      icon="ph:info-bold"
                      className="w-5 h-5 text-[#FC480D] flex-shrink-0 mt-0.5"
                    />
                    <p className="text-xs sm:text-sm text-orange-900 leading-relaxed font-medium">
                      Atur pembayaran, slot pemain, dan biaya operasional event. Semua nilai bisa diubah lagi setelah event tayang.
                    </p>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
                    {/* Left Column: Pembayaran & Pendaftaran */}
                    <div className="md:col-span-6 space-y-6">
                      {/* Pembayaran Card */}
                      <div className="bg-white rounded-2xl border border-gray-200/80 p-5 sm:p-6 space-y-6 shadow-xs">
                        <div className="border-b border-gray-100 pb-3">
                          <h3 className="text-lg font-bold text-gray-900">Pembayaran</h3>
                        </div>

                        {/* Tipe Mabar */}
                        <div className="space-y-2.5 !mt-0">
                          <label className="block text-sm font-bold text-gray-900">Tipe Mabar</label>
                          <div className="grid grid-cols-2 gap-3">
                            <button
                              type="button"
                              onClick={() => setFieldValue('biayaPemain', 'free')}
                              className={`p-3.5 rounded-xl border-2 text-xs sm:text-sm font-bold flex items-center justify-between transition-all cursor-pointer ${
                                values.biayaPemain === 'free'
                                  ? 'border-[#FC480D] bg-orange-50/50 text-[#FC480D]'
                                  : 'border-gray-200 bg-white text-gray-600 hover:border-gray-300'
                              }`}
                            >
                              <div className="flex items-center gap-2">
                                <Icon icon="ph:gift-bold" className="w-5 h-5" />
                                <span>Gratis</span>
                              </div>
                              <div
                                className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${
                                  values.biayaPemain === 'free'
                                    ? 'border-[#FC480D] bg-[#FC480D]'
                                    : 'border-gray-300'
                                }`}
                              >
                                {values.biayaPemain === 'free' && (
                                  <Icon icon="ph:check-bold" className="w-2.5 h-2.5 text-white" />
                                )}
                              </div>
                            </button>

                            <button
                              type="button"
                              onClick={() => setFieldValue('biayaPemain', 'paid')}
                              className={`p-3.5 rounded-xl border-2 text-xs sm:text-sm font-bold flex items-center justify-between transition-all cursor-pointer ${
                                values.biayaPemain === 'paid'
                                  ? 'border-[#FC480D] bg-orange-50/50 text-[#FC480D]'
                                  : 'border-gray-200 bg-white text-gray-600 hover:border-gray-300'
                              }`}
                            >
                              <div className="flex items-center gap-2">
                                <Icon icon="ph:money-bold" className="w-5 h-5" />
                                <span>Berbayar</span>
                              </div>
                              <div
                                className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${
                                  values.biayaPemain === 'paid'
                                    ? 'border-[#FC480D] bg-[#FC480D]'
                                    : 'border-gray-300'
                                }`}
                              >
                                {values.biayaPemain === 'paid' && (
                                  <Icon icon="ph:check-bold" className="w-2.5 h-2.5 text-white" />
                                )}
                              </div>
                            </button>
                          </div>
                        </div>

                        {/* Paid Settings */}
                        {values.biayaPemain === 'paid' && (
                          <div className="space-y-6 pt-2 !mt-3">
                            {/* Waktu Pembayaran */}
                            <div className="space-y-2.5">
                              <label className="block text-sm font-bold text-gray-900">
                                Waktu Pembayaran
                              </label>
                              <div className="grid grid-cols-2 gap-3">
                                <button
                                  type="button"
                                  disabled={!hasActivePayment}
                                  onClick={() => {
                                    setFieldValue('paymentTime', 'awal');
                                    setFieldValue('paymentMethod', 'qris');
                                    setFieldValue('konfirmasiOtomatis', 'yes');
                                  }}
                                  className={`p-3.5 rounded-xl border-2 text-xs sm:text-sm font-bold flex items-center justify-between transition-all cursor-pointer disabled:opacity-50 ${
                                    values.paymentTime === 'awal'
                                      ? 'border-[#FC480D] bg-orange-50/50 text-[#FC480D]'
                                      : 'border-gray-200 bg-white text-gray-600 hover:border-gray-300'
                                  }`}
                                >
                                  <div className="flex items-center gap-2">
                                    <Icon icon="ph:clock-clockwise-bold" className="w-5 h-5" />
                                    <span>Di awal</span>
                                  </div>
                                  <div
                                    className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${
                                      values.paymentTime === 'awal'
                                        ? 'border-[#FC480D] bg-[#FC480D]'
                                        : 'border-gray-300'
                                    }`}
                                  >
                                    {values.paymentTime === 'awal' && (
                                      <Icon icon="ph:check-bold" className="w-2.5 h-2.5 text-white" />
                                    )}
                                  </div>
                                </button>

                                <button
                                  type="button"
                                  onClick={() => setFieldValue('paymentTime', 'akhir')}
                                  className={`p-3.5 rounded-xl border-2 text-xs sm:text-sm font-bold flex items-center justify-between transition-all cursor-pointer ${
                                    values.paymentTime === 'akhir'
                                      ? 'border-[#FC480D] bg-orange-50/50 text-[#FC480D]'
                                      : 'border-gray-200 bg-white text-gray-600 hover:border-gray-300'
                                  }`}
                                >
                                  <div className="flex items-center gap-2">
                                    <Icon icon="ph:clock-counter-clockwise-bold" className="w-5 h-5" />
                                    <span>Di akhir</span>
                                  </div>
                                  <div
                                    className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${
                                      values.paymentTime === 'akhir'
                                        ? 'border-[#FC480D] bg-[#FC480D]'
                                        : 'border-gray-300'
                                    }`}
                                  >
                                    {values.paymentTime === 'akhir' && (
                                      <Icon icon="ph:check-bold" className="w-2.5 h-2.5 text-white" />
                                    )}
                                  </div>
                                </button>
                              </div>
                            </div>

                            {/* Metode Pembayaran */}
                            <div className="space-y-2.5 !mt-3">
                              <label className="block text-sm font-bold text-gray-900">
                                Metode Pembayaran
                              </label>
                              <div className="grid grid-cols-2 gap-3">
                                <button
                                  type="button"
                                  disabled={values.paymentTime === 'awal'}
                                  onClick={() => setFieldValue('paymentMethod', 'manual')}
                                  className={`p-3.5 rounded-xl border-2 text-xs sm:text-sm font-bold flex items-center justify-between transition-all cursor-pointer disabled:opacity-50 ${
                                    values.paymentMethod === 'manual'
                                      ? 'border-[#FC480D] bg-orange-50/50 text-[#FC480D]'
                                      : 'border-gray-200 bg-white text-gray-600 hover:border-gray-300'
                                  }`}
                                >
                                  <div className="flex items-center gap-2">
                                    <Icon icon="ph:hand-coins-bold" className="w-5 h-5" />
                                    <span>Manual</span>
                                  </div>
                                  <div
                                    className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${
                                      values.paymentMethod === 'manual'
                                        ? 'border-[#FC480D] bg-[#FC480D]'
                                        : 'border-gray-300'
                                    }`}
                                  >
                                    {values.paymentMethod === 'manual' && (
                                      <Icon icon="ph:check-bold" className="w-2.5 h-2.5 text-white" />
                                    )}
                                  </div>
                                </button>

                                <button
                                  type="button"
                                  disabled={values.paymentTime === 'awal' || !hasActivePayment}
                                  onClick={() => setFieldValue('paymentMethod', 'qris')}
                                  className={`p-3.5 rounded-xl border-2 text-xs sm:text-sm font-bold flex items-center justify-between transition-all cursor-pointer disabled:opacity-50 ${
                                    values.paymentMethod === 'qris'
                                      ? 'border-[#FC480D] bg-orange-50/50 text-[#FC480D]'
                                      : 'border-gray-200 bg-white text-gray-600 hover:border-gray-300'
                                  }`}
                                >
                                  <div className="flex items-center gap-2">
                                    <Icon icon="ph:qr-code-bold" className="w-5 h-5" />
                                    <span>QRIS</span>
                                  </div>
                                  <div
                                    className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${
                                      values.paymentMethod === 'qris'
                                        ? 'border-[#FC480D] bg-[#FC480D]'
                                        : 'border-gray-300'
                                    }`}
                                  >
                                    {values.paymentMethod === 'qris' && (
                                      <Icon icon="ph:check-bold" className="w-2.5 h-2.5 text-white" />
                                    )}
                                  </div>
                                </button>
                              </div>

                              {!hasActivePayment && (
                                <div className="p-3 bg-amber-50 border border-amber-200 rounded-xl text-amber-800 text-xs flex items-start gap-2">
                                  <Icon
                                    icon="ph:warning-bold"
                                    className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5"
                                  />
                                  <div>
                                    <strong>Pemberitahuan:</strong> Fitur pembayaran QRIS dan Pembayaran di Awal dinonaktifkan karena wallet pembayaran komunitas Anda belum aktif/terverifikasi. Silakan ajukan KYC terlebih dahulu di menu Wallet.
                                  </div>
                                </div>
                              )}

                              {values.paymentMethod === 'qris' && (
                                <div className="p-3 bg-amber-50/90 border border-amber-200 rounded-xl text-amber-900 text-xs flex items-start gap-2.5">
                                  <Icon
                                    icon="ph:warning-circle-bold"
                                    className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5"
                                  />
                                  <p className="leading-relaxed text-[11px] font-medium">
                                    Setiap transaksi yang dibayar via QRIS akan dikenakan biaya transaksi yang dibebankan kepada komunitas.
                                  </p>
                                </div>
                              )}
                            </div>
                          </div>
                        )}
                      </div>

                      {/* Pendaftaran Card */}
                      <div className="bg-white rounded-2xl border border-gray-200/80 p-5 sm:p-6 space-y-6 shadow-xs">
                        <div className="border-b border-gray-100 pb-3">
                          <h3 className="text-lg font-bold text-gray-900">Pendaftaran</h3>
                        </div>

                        {/* Host Approval Switch */}
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-sm font-bold text-gray-900">Host approval</p>
                            <p className="text-xs text-gray-500">
                              {values.paymentTime === 'awal'
                                ? 'Nonaktif otomatis karena pembayaran di awal.'
                                : 'Aktifkan jika player perlu disetujui sebelum masuk slot.'}
                            </p>
                          </div>
                          <label className="relative inline-flex items-center cursor-pointer">
                            <input
                              type="checkbox"
                              disabled={values.paymentTime === 'awal'}
                              checked={values.konfirmasiOtomatis === 'no'}
                              onChange={(e) =>
                                setFieldValue('konfirmasiOtomatis', e.target.checked ? 'no' : 'yes')
                              }
                              className="sr-only peer"
                            />
                            <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none rounded-full peer peer-disabled:opacity-50 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#FC480D]"></div>
                          </label>
                        </div>

                        {/* Waiting List Switch */}
                        <div className="pt-4 border-t border-gray-100 space-y-4">
                          <div className="flex items-start justify-between gap-4">
                            <div>
                              <p className="text-sm font-bold text-gray-900">Waiting List</p>
                              <p className="text-xs text-gray-500 mt-0.5 leading-relaxed">
                                Pendaftaran tetap dibuka meski kuota penuh. Pemain masuk daftar tunggu dan otomatis naik jadi peserta kalau ada yang unlist.
                              </p>
                            </div>
                            <label className="relative inline-flex items-center cursor-pointer shrink-0 mt-0.5">
                              <input
                                type="checkbox"
                                checked={values.waitingList}
                                onChange={(e) => setFieldValue('waitingList', e.target.checked)}
                                className="sr-only peer"
                              />
                              <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#FC480D]"></div>
                            </label>
                          </div>

                          {/* Stepper Card */}
                          {values.waitingList && (
                            <div className="p-3.5 sm:p-4 rounded-2xl bg-[#F8FAFC] border border-gray-100 flex items-center justify-between gap-3">
                              <div className="flex items-center gap-3 min-w-0">
                                <div className="w-9 h-9 rounded-xl bg-gray-100/80 flex items-center justify-center text-gray-400 shrink-0">
                                  <Icon icon="ph:users-three-bold" className="w-5 h-5" />
                                </div>
                                <div className="min-w-0">
                                  <p className="text-sm font-bold text-gray-900 leading-snug">
                                    Batas daftar tunggu
                                  </p>
                                  <p className="text-[11px] sm:text-xs text-gray-500 leading-tight">
                                    Maksimal {values.waitingListSlots} pemain menunggu, urut sesuai waktu daftar
                                  </p>
                                </div>
                              </div>

                              <div className="flex items-center gap-2 shrink-0">
                                <button
                                  type="button"
                                  onClick={() => {
                                    setFieldValue(
                                      'waitingListSlots',
                                      Math.max(1, (values.waitingListSlots || 1) - 1)
                                    );
                                  }}
                                  className="w-8 h-8 rounded-full border border-gray-200 bg-white flex items-center justify-center text-gray-600 hover:bg-gray-50 hover:border-gray-300 font-bold transition-all cursor-pointer shadow-2xs"
                                >
                                  -
                                </button>
                                <span className="w-7 text-center font-bold text-sm text-gray-900 select-none">
                                  {values.waitingListSlots || 1}
                                </span>
                                <button
                                  type="button"
                                  onClick={() => {
                                    setFieldValue(
                                      'waitingListSlots',
                                      (values.waitingListSlots || 1) + 1
                                    );
                                  }}
                                  className="w-8 h-8 rounded-full border border-gray-200 bg-white flex items-center justify-center text-gray-600 hover:bg-gray-50 hover:border-gray-300 font-bold transition-all cursor-pointer shadow-2xs"
                                >
                                  +
                                </button>
                              </div>
                            </div>
                          )}
                        </div>

                        {/* Sparring Form */}
                        {(() => {
                          const selectedCat = sportCategories.find(
                            (c) => c.slug === values.kategoriOlahraga
                          );
                          const isSparringSport = isSparringSupportedSport(selectedCat);

                          return isSparringSport ? (
                            <div className="pt-4 border-t border-gray-100">
                              <SparringForm
                                isSparring={isSparring}
                                onToggleSparring={setIsSparring}
                                teamAName={teamAName}
                                onTeamANameChange={setTeamAName}
                                teamAColor={teamAColor}
                                onTeamAColorChange={setTeamAColor}
                                teamBName={teamBName}
                                onTeamBNameChange={setTeamBName}
                                teamBColor={teamBColor}
                                onTeamBColorChange={setTeamBColor}
                                embedded={true}
                              />
                            </div>
                          ) : null;
                        })()}
                      </div>
                    </div>

                    {/* Right Column: Posisi & Operasional */}
                    <div className="md:col-span-6 space-y-6">
                      {/* Formulir Tambahan */}
                      <CustomFormSection
                        fields={customFormFields}
                        sportCategorySlug={values.kategoriOlahraga}
                        onChange={setCustomFormFields}
                        enabled={customFormEnabled}
                        onToggleEnabled={setCustomFormEnabled}
                      />

                      {/* Slot & Biaya per Posisi */}
                      <div className="bg-white rounded-2xl border border-gray-200/80 p-5 sm:p-6 space-y-4 shadow-xs">
                        <div className="flex items-center justify-between border-b border-gray-100 pb-3">
                          <h3 className="text-lg font-bold text-gray-900">Slot & Biaya per Posisi</h3>
                        </div>

                        <div className="space-y-3">
                          {playerRoles.map((role, index) => (
                            <div
                              key={role.id || index}
                              className="p-4 rounded-xl border border-gray-200 space-y-3 bg-white hover:border-gray-300 transition-all"
                            >
                              <div className="flex items-center justify-between gap-2">
                                <input
                                  type="text"
                                  value={role.role}
                                  onChange={(e) => {
                                    const updated = [...playerRoles];
                                    updated[index].role = e.target.value;
                                    setPlayerRoles(updated);
                                  }}
                                  placeholder="Nama posisi (mis. Kiper, Pemain)"
                                  className="font-bold text-sm text-gray-900 border-b border-transparent hover:border-gray-300 focus:border-[#FC480D] focus:outline-none py-1 flex-1"
                                />
                                {playerRoles.length > 1 && (
                                  <button
                                    type="button"
                                    onClick={() => {
                                      setPlayerRoles(playerRoles.filter((_, i) => i !== index));
                                    }}
                                    className="p-1.5 text-red-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                                  >
                                    <Icon icon="ph:trash-bold" className="w-4 h-4" />
                                  </button>
                                )}
                              </div>

                              <div className="grid grid-cols-2 gap-4 items-center">
                                <div>
                                  <label className="block text-xs text-gray-500 font-medium mb-1">
                                    Jumlah
                                  </label>
                                  <div className="flex items-center gap-2">
                                    <button
                                      type="button"
                                      onClick={() => {
                                        const updated = [...playerRoles];
                                        if (updated[index].jumlahPemain > 1) {
                                          updated[index].jumlahPemain -= 1;
                                          setPlayerRoles(updated);
                                        }
                                      }}
                                      className="w-8 h-8 rounded-lg bg-gray-100 flex items-center justify-center text-gray-700 hover:bg-gray-200 font-bold transition-colors"
                                    >
                                      -
                                    </button>
                                     <input
                                       type="number"
                                       min={1}
                                       value={role.jumlahPemain || ''}
                                       onChange={(e) => {
                                         const val = Math.max(1, parseInt(e.target.value) || 1);
                                         const updated = [...playerRoles];
                                         updated[index].jumlahPemain = val;
                                         setPlayerRoles(updated);
                                       }}
                                       className="w-12 text-center text-sm font-medium text-gray-900 border border-gray-200 rounded-lg py-1 focus:outline-none focus:ring-2 focus:ring-[#FC480D] [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                                     />
                                    <button
                                      type="button"
                                      onClick={() => {
                                        const updated = [...playerRoles];
                                        updated[index].jumlahPemain += 1;
                                        setPlayerRoles(updated);
                                      }}
                                      className="w-8 h-8 rounded-lg bg-orange-50 text-[#FC480D] flex items-center justify-center hover:bg-orange-100 font-bold transition-colors"
                                    >
                                      +
                                    </button>
                                  </div>
                                </div>

                                <div>
                                  <label className="block text-xs text-gray-500 font-medium mb-1">
                                    Biaya / orang
                                  </label>
                                  <div className="relative">
                                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-xs font-semibold text-gray-400">
                                      Rp.
                                    </span>
                                    <input
                                      type="number"
                                      disabled={values.biayaPemain === 'free'}
                                      value={values.biayaPemain === 'free' ? 0 : role.biayaPerOrang}
                                      onChange={(e) => {
                                        const updated = [...playerRoles];
                                        updated[index].biayaPerOrang = Math.max(
                                          0,
                                          parseInt(e.target.value) || 0
                                        );
                                        setPlayerRoles(updated);
                                      }}
                                      placeholder="0"
                                      className={`w-full border border-gray-200 rounded-xl pl-9 pr-3 py-2 text-sm font-semibold focus:outline-none focus:ring-2 focus:ring-[#FC480D] ${
                                        values.biayaPemain === 'free'
                                          ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                                          : 'text-[#FC480D]'
                                      }`}
                                    />
                                  </div>
                                </div>
                              </div>
                            </div>
                          ))}

                          <button
                            type="button"
                            onClick={() => {
                              setPlayerRoles([
                                ...playerRoles,
                                {
                                  id: `role-${Date.now()}`,
                                  role: '',
                                  jumlahPemain: 1,
                                  biayaPerOrang: values.biayaPemain === 'free' ? 0 : 50000,
                                },
                              ]);
                            }}
                            className="w-full py-3 rounded-xl border border-dashed border-orange-300 bg-orange-50/50 text-[#FC480D] font-bold text-xs sm:text-sm flex items-center justify-center gap-2 hover:bg-orange-50 transition-all cursor-pointer"
                          >
                            <Icon icon="ph:plus-bold" className="w-4 h-4" />
                            <span>Tambah Posisi</span>
                          </button>
                        </div>

                        {/* Summary Box */}
                        <div className="bg-gray-50 rounded-xl p-4 space-y-2 border border-gray-100 mt-4">
                          <div className="flex items-center justify-between text-xs sm:text-sm">
                            <span className="text-gray-500 font-medium">Total Slot</span>
                            <span className="font-bold text-gray-900">{calculateTotalSlots()} slot</span>
                          </div>
                          <div className="flex items-center justify-between text-xs sm:text-sm">
                            <span className="text-gray-500 font-medium">Total Biaya Pemain</span>
                            <span className="font-bold text-[#FC480D]">
                              {values.biayaPemain === 'free'
                                ? 'Free'
                                : formatCurrency(
                                    playerRoles.reduce(
                                      (sum, r) => sum + r.jumlahPemain * r.biayaPerOrang,
                                      0
                                    )
                                  )}
                            </span>
                          </div>
                        </div>
                      </div>

                      {/* Biaya Operasional Section */}
                      <div className="bg-white rounded-2xl border border-gray-200/80 p-5 sm:p-6 space-y-4 shadow-xs">
                        <div className="flex items-center justify-between border-b border-gray-100 pb-3">
                          <h3 className="text-lg font-bold text-gray-900">Biaya Operasional</h3>
                        </div>

                        <div className="space-y-3">
                          {operationalCosts.map((cost, index) => {
                            const currentQty = cost.jumlahItem ?? 1;
                            const currentUnitPrice = cost.biayaPerItem ?? cost.biaya ?? 0;

                            return (
                              <div
                                key={cost.id || index}
                                className="p-4 rounded-xl border border-gray-200 space-y-3 bg-white hover:border-gray-300 transition-all"
                              >
                                <div className="flex items-center justify-between gap-2">
                                  <input
                                    type="text"
                                    value={cost.deskripsi}
                                    onChange={(e) => {
                                      const updated = [...operationalCosts];
                                      updated[index].deskripsi = e.target.value;
                                      setOperationalCosts(updated);
                                    }}
                                    placeholder="Nama pengeluaran"
                                    className="font-bold text-sm text-gray-900 border-b border-transparent hover:border-gray-300 focus:border-[#FC480D] focus:outline-none py-1 flex-1 placeholder:font-normal placeholder:text-gray-400"
                                  />
                                  <button
                                    type="button"
                                    onClick={() => {
                                      setOperationalCosts(
                                        operationalCosts.filter((_, i) => i !== index)
                                      );
                                    }}
                                    className="p-1.5 text-red-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                                  >
                                    <Icon icon="ph:trash-bold" className="w-4 h-4" />
                                  </button>
                                </div>

                                <div className="grid grid-cols-2 gap-4 items-center">
                                  <div>
                                    <label className="block text-xs text-gray-500 font-medium mb-1">
                                      Jumlah
                                    </label>
                                    <div className="flex items-center gap-2">
                                      <button
                                        type="button"
                                        onClick={() => {
                                          const updated = [...operationalCosts];
                                          const newQty = Math.max(1, currentQty - 1);
                                          updated[index] = {
                                            ...updated[index],
                                            jumlahItem: newQty,
                                            biayaPerItem: currentUnitPrice,
                                            biaya: newQty * currentUnitPrice,
                                          };
                                          setOperationalCosts(updated);
                                        }}
                                        className="w-8 h-8 rounded-lg bg-gray-100 flex items-center justify-center text-gray-700 hover:bg-gray-200 font-bold transition-colors"
                                      >
                                        -
                                      </button>
                                       <input
                                         type="number"
                                         min={1}
                                         value={currentQty || ''}
                                         onChange={(e) => {
                                           const newQty = Math.max(1, parseInt(e.target.value) || 1);
                                           const updated = [...operationalCosts];
                                           updated[index] = {
                                             ...updated[index],
                                             jumlahItem: newQty,
                                             biayaPerItem: currentUnitPrice,
                                             biaya: newQty * currentUnitPrice,
                                           };
                                           setOperationalCosts(updated);
                                         }}
                                         className="w-12 text-center text-sm font-medium text-gray-900 border border-gray-200 rounded-lg py-1 focus:outline-none focus:ring-2 focus:ring-[#FC480D] [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                                       />
                                      <button
                                        type="button"
                                        onClick={() => {
                                          const updated = [...operationalCosts];
                                          const newQty = currentQty + 1;
                                          updated[index] = {
                                            ...updated[index],
                                            jumlahItem: newQty,
                                            biayaPerItem: currentUnitPrice,
                                            biaya: newQty * currentUnitPrice,
                                          };
                                          setOperationalCosts(updated);
                                        }}
                                        className="w-8 h-8 rounded-lg bg-orange-50 text-[#FC480D] flex items-center justify-center hover:bg-orange-100 font-bold transition-colors"
                                      >
                                        +
                                      </button>
                                    </div>
                                  </div>

                                  <div>
                                    <label className="block text-xs text-gray-500 font-medium mb-1">
                                      Biaya
                                    </label>
                                    <div className="relative">
                                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-xs font-semibold text-gray-400">
                                        Rp.
                                      </span>
                                      <input
                                        type="number"
                                        value={currentUnitPrice}
                                        onChange={(e) => {
                                          const updated = [...operationalCosts];
                                          const newPrice = Math.max(
                                            0,
                                            parseInt(e.target.value) || 0
                                          );
                                          updated[index] = {
                                            ...updated[index],
                                            jumlahItem: currentQty,
                                            biayaPerItem: newPrice,
                                            biaya: currentQty * newPrice,
                                          };
                                          setOperationalCosts(updated);
                                        }}
                                        placeholder="0"
                                        className="w-full border border-gray-200 rounded-xl pl-9 pr-3 py-2 text-sm font-medium text-gray-900 focus:outline-none focus:ring-2 focus:ring-[#FC480D]"
                                      />
                                    </div>
                                  </div>
                                </div>
                              </div>
                            );
                          })}

                          <button
                            type="button"
                            onClick={() => {
                              setOperationalCosts([
                                ...operationalCosts,
                                {
                                  id: `cost-${Date.now()}`,
                                  deskripsi: '',
                                  jumlahItem: 1,
                                  biayaPerItem: 0,
                                  biaya: 0,
                                },
                              ]);
                            }}
                            className="w-full py-3 rounded-xl border border-dashed border-orange-300 bg-orange-50/50 text-[#FC480D] font-bold text-xs sm:text-sm flex items-center justify-center gap-2 hover:bg-orange-50 transition-all cursor-pointer"
                          >
                            <Icon icon="ph:plus-bold" className="w-4 h-4" />
                            <span>Tambah Pengeluaran</span>
                          </button>
                        </div>

                        {/* Summary Box */}
                        <div className="bg-gray-50 rounded-xl p-4 flex items-center justify-between border border-gray-100 text-xs sm:text-sm mt-4">
                          <span className="text-gray-500 font-medium">Total Biaya Operasional</span>
                          <span className="font-bold text-[#FC480D]">
                            Rp. {calculateTotalOperationalCost().toLocaleString('id-ID')}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Bottom Sticky Action Bar */}
              <div className="fixed bottom-0 left-0 right-0 md:left-64 bg-white border-t border-gray-200 p-4 z-40 shadow-lg">
                <div className="max-w-5xl mx-auto flex items-center justify-between gap-3 px-0 sm:px-4">
                  {currentStep > 1 && (
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => setCurrentStep((prev) => Math.max(1, prev - 1))}
                      className="rounded-full px-6 border-gray-300 text-gray-700 font-semibold flex-none"
                    >
                      Kembali
                    </Button>
                  )}

                  {currentStep < 3 ? (
                    <Button
                      key="next-step-btn"
                      type="button"
                      variant="primary"
                      fullWidth
                      onClick={() => handleNextStep(validateForm, setFieldTouched)}
                      className="rounded-full px-8 bg-[#FC480D] hover:bg-[#e03d05] border-none text-white font-bold flex items-center justify-center gap-2 flex-1 md:flex-none md:ml-auto md:min-w-[160px]"
                    >
                      <span>Lanjut</span>
                      <Icon icon="ph:arrow-right-bold" className="w-4 h-4" />
                    </Button>
                  ) : (
                    <Button
                      key="submit-event-btn"
                      type="submit"
                      variant="primary"
                      fullWidth
                      disabled={isSubmitting}
                      className="rounded-full px-8 bg-[#FC480D] hover:bg-[#e03d05] border-none text-white font-bold flex items-center justify-center gap-2 flex-1 md:flex-none md:ml-auto md:min-w-[200px]"
                    >
                      <span>{isSubmitting ? 'Menyimpan...' : 'Publikasikan Event'}</span>
                      <Icon icon="ph:check-bold" className="w-4 h-4" />
                    </Button>
                  )}
                </div>
              </div>

              {/* Date Picker Modal */}
              {isDatePickerOpen && (
                <div className="fixed inset-0 z-50 flex items-end md:items-center justify-center bg-black/50 backdrop-blur-xs transition-all">
                  <div
                    className="fixed inset-0"
                    onClick={() => setIsDatePickerOpen(false)}
                  />
                  <div className="relative w-full max-w-md bg-white rounded-t-3xl md:rounded-3xl p-5 sm:p-6 shadow-2xl z-10 animate-in slide-in-from-bottom md:zoom-in-95 duration-200">
                    {/* Drag handle indicator for mobile */}
                    <div className="w-10 h-1 bg-gray-200 rounded-full mx-auto mb-4 md:hidden" />

                    {/* Header */}
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-lg font-bold text-gray-900">Pilih Tanggal</h3>
                      <button
                        type="button"
                        onClick={() => setIsDatePickerOpen(false)}
                        className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center text-gray-500 hover:bg-gray-200 transition-colors"
                      >
                        <Icon icon="ph:x-bold" className="w-4 h-4" />
                      </button>
                    </div>

                    {/* Month Navigator */}
                    <div className="flex items-center justify-between mb-4 px-2">
                      <button
                        type="button"
                        onClick={() => {
                          if (calendarMonth === 0) {
                            setCalendarMonth(11);
                            setCalendarYear((y) => y - 1);
                          } else {
                            setCalendarMonth((m) => m - 1);
                          }
                        }}
                        className="w-9 h-9 rounded-xl bg-gray-100 flex items-center justify-center text-gray-700 hover:bg-gray-200 transition-colors cursor-pointer"
                      >
                        <Icon icon="ph:caret-left-bold" className="w-5 h-5" />
                      </button>
                      <span className="font-bold text-base text-gray-900">
                        {indonesianMonths[calendarMonth]} {calendarYear}
                      </span>
                      <button
                        type="button"
                        onClick={() => {
                          if (calendarMonth === 11) {
                            setCalendarMonth(0);
                            setCalendarYear((y) => y + 1);
                          } else {
                            setCalendarMonth((m) => m + 1);
                          }
                        }}
                        className="w-9 h-9 rounded-xl bg-gray-100 flex items-center justify-center text-gray-700 hover:bg-gray-200 transition-colors cursor-pointer"
                      >
                        <Icon icon="ph:caret-right-bold" className="w-5 h-5" />
                      </button>
                    </div>

                    {/* Weekdays Header */}
                    <div className="grid grid-cols-7 gap-1 text-center mb-2">
                      {dayNamesIndonesian.map((day) => (
                        <span key={day} className="text-xs font-bold text-gray-400 py-1">
                          {day}
                        </span>
                      ))}
                    </div>

                    {/* Days Grid */}
                    <div className="grid grid-cols-7 gap-2 text-center">
                      {getDaysInMonthMatrix(calendarYear, calendarMonth).map((day, idx) => {
                        if (!day) {
                          return <div key={`empty-${idx}`} />;
                        }

                        const monthStr = String(calendarMonth + 1).padStart(2, '0');
                        const dayStr = String(day).padStart(2, '0');
                        const dateFormatted = `${calendarYear}-${monthStr}-${dayStr}`;

                        const isSelected = values.tanggalEvent === dateFormatted;

                        return (
                          <button
                            key={dateFormatted}
                            type="button"
                            onClick={() => {
                              setFieldValue('tanggalEvent', dateFormatted);
                              setIsDatePickerOpen(false);
                            }}
                            className={`h-10 rounded-xl font-bold text-sm flex items-center justify-center transition-all cursor-pointer ${
                              isSelected
                                ? 'bg-[#FC480D] text-white shadow-md'
                                : 'bg-white border border-gray-200 text-gray-800 hover:border-orange-300 hover:bg-orange-50/50'
                            }`}
                          >
                            {day}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                </div>
              )}
            </Form>
          )}
        </Formik>
      </div>


      {/* Confirmation Modal */}
      <Modal
        isOpen={isConfirmModalOpen}
        onClose={() => setIsConfirmModalOpen(false)}
        title="Konfirmasi Simpan"
        size="sm"
      >
        <div className="space-y-4">
          <p className="text-gray-600 text-sm">
            Apakah Anda yakin ingin publikasikan event mabar ini?
          </p>
          <div className="flex items-center justify-end gap-3 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => setIsConfirmModalOpen(false)}
              className="rounded-full px-5 text-gray-600 border-gray-300"
            >
              Batal
            </Button>
            <Button
              type="button"
              variant="primary"
              onClick={handleConfirmedSave}
              loading={isSubmitting}
              className="rounded-full px-6 bg-[#FC480D] hover:bg-[#e03d05] border-none text-white font-bold"
            >
              Ya, Publikasikan
            </Button>
          </div>
        </div>
      </Modal>

      {/* Notification */}
      <Notification
        isOpen={notification.isOpen}
        onClose={() => setNotification({ ...notification, isOpen: false })}
        title={notification.title}
        message={notification.message}
        variant={notification.variant}
      />
    </div>
  );
};

export default CreateMabarCommunityPage;
